import React, { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  MessageSquare, Database, Users, Calendar, Wrench, Settings as SettingsIcon, Plus, LogOut, Sun, Moon, FileText, Inbox, Mail, Shield,
} from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { api } from "@/lib/api";
import { toast } from "sonner";

const hexToRgba = (hex, a) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const NAV = [
  { to: "/chat", label: "Chat", icon: MessageSquare, testid: "nav-chat" },
  { to: "/vault", label: "Vault", icon: Database, testid: "nav-vault" },
  { to: "/people", label: "People", icon: Users, testid: "nav-people" },
  { to: "/meetings", label: "Meetings", icon: Calendar, testid: "nav-meetings" },
  { to: "/inbox", label: "Inbox", icon: Mail, testid: "nav-inbox" },
  { to: "/briefings", label: "Briefings", icon: Inbox, testid: "nav-briefings" },
  { to: "/studio", label: "Studio", icon: FileText, testid: "nav-studio" },
  { to: "/build", label: "Build Mode", icon: Wrench, badge: "NEW", testid: "nav-build" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Sidebar({ mobileOpen = false, onClose }) {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [recent, setRecent] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sources, setSources] = useState([]);
  const [pendingBriefs, setPendingBriefs] = useState(0);
  const [activeCategory, setActiveCategory] = useState(() => {
    try { return localStorage.getItem("disciple_cat") || "All conversations"; }
    catch { return "All conversations"; }
  });

  useEffect(() => {
    let mounted = true;
    const refresh = async () => {
      try {
        const [r, c, s] = await Promise.all([
          api.get("/conversations"),
          api.get("/conversations/categories"),
          api.get("/sources"),
        ]);
        if (!mounted) return;
        setRecent(r.data);
        setCategories(c.data);
        setSources(s.data);
        try {
          const p = await api.get("/proposals");
          if (mounted) setPendingBriefs(p.data.filter((x) => x.status === "pending").length);
        } catch { /* ignore */ }
      } catch { /* ignore */ }
    };
    refresh();
    // Light polling so counts stay fresh after sending messages
    const t = setInterval(refresh, 10000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const newConversation = async () => {
    onClose?.();
    const r = await api.post("/conversations");
    navigate(`/chat/${r.data.id}`);
    setRecent((prev) => [{ id: r.data.id, title: "New conversation", updated_at: r.data.updated_at, category: "Topic / Strategy" }, ...prev].slice(0, 80));
  };

  const filteredRecent = recent.filter((c) =>
    activeCategory === "All conversations" ? true : (c.category || "Topic / Strategy") === activeCategory
  ).slice(0, 8);

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const navItems = user?.role === "super_admin"
    ? [...NAV, { to: "/admin", label: "Super Admin", icon: Shield, testid: "nav-admin" }]
    : NAV;

  const pickCategory = (key) => {
    setActiveCategory(key);
    try { localStorage.setItem("disciple_cat", key); } catch { /* ignore */ }
  };

  const colorTimers = useRef({});
  const updateCategoryColor = (key, color) => {
    setCategories((prev) => prev.map((c) => (c.key === key ? { ...c, color } : c)));
    clearTimeout(colorTimers.current[key]);
    colorTimers.current[key] = setTimeout(async () => {
      try {
        await api.put("/conversations/categories/color", { key, color });
      } catch {
        toast.error("Couldn't save the category color.");
      }
    }, 350);
  };

  return (
    <aside className={`sidebar ${mobileOpen ? "mobile-open" : ""}`} data-testid="sidebar">
      {/* Brand */}
      <div className="sidebar-section" style={{ paddingBottom: 8 }}>
        <div className="brandmark" style={{ justifyContent: "center" }}>
          <Logo height={38} />
        </div>
      </div>

      {/* New conversation */}
      <div className="sidebar-section" style={{ paddingTop: 6 }}>
        <button
          onClick={newConversation}
          data-testid="new-conversation-btn"
          className="btn btn-primary"
          style={{ width: "100%", justifyContent: "center", padding: "13px 16px", borderRadius: 8, fontSize: 14 }}
        >
          <Plus size={18} strokeWidth={2.2} /> New conversation
        </button>
      </div>

      {/* Nav */}
      <nav className="sidebar-section" style={{ paddingTop: 6 }}>
        {navItems.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            onClick={onClose}
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            data-testid={n.testid}
          >
            <n.icon size={18} strokeWidth={2} />
            <span>{n.label}</span>
            {n.to === "/briefings" && pendingBriefs > 0 && (
              <span className="badge" data-testid="briefings-badge" style={{
                background: "rgba(78,205,196,0.18)", color: "var(--ph-product-teal)",
                border: "1px solid rgba(78,205,196,0.4)",
              }}>{pendingBriefs}</span>
            )}
            {n.badge && <span className="badge">{n.badge}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Scrollable middle: Recent + Category + Connected Sources */}
      <div className="sidebar-section" style={{ flex: 1, overflowY: "auto", paddingTop: 14 }}>
        {/* Recent */}
        <div className="section-label">Recent</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 1, marginBottom: 18 }}>
          {filteredRecent.length === 0 && (
            <div className="text-dim" style={{ fontSize: 12, padding: "8px 10px" }}>
              {recent.length === 0 ? "No conversations yet." : "Nothing in this category."}
            </div>
          )}
          {filteredRecent.map((c) => (
            <NavLink
              key={c.id}
              to={`/chat/${c.id}`}
              onClick={onClose}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              data-testid={`recent-${c.id}`}
              style={{ fontSize: 12.5, color: "var(--ink-dim)" }}
            >
              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {c.title || "New conversation"}
              </span>
            </NavLink>
          ))}
        </div>

        {/* CATEGORY */}
        <div className="section-label">Category</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 1, marginBottom: 18 }} data-testid="categories">
          {categories.map((cat) => {
            const active = activeCategory === cat.key;
            const color = cat.color || "#6B5CE7";
            return (
              <div
                key={cat.key}
                onClick={() => pickCategory(cat.key)}
                role="button"
                tabIndex={0}
                data-testid={`category-${cat.key}`}
                className={`nav-item ${active ? "active" : ""}`}
                style={{
                  width: "100%",
                  background: active ? "var(--panel-2)" : "transparent",
                  border: "none", padding: "6px 10px",
                  display: "flex", alignItems: "center", gap: 10,
                  fontSize: 13, color: active ? "var(--ink)" : "var(--ink-soft)",
                  cursor: "pointer", textAlign: "left",
                }}
              >
                <label
                  className="cat-swatch"
                  title="Change color"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`category-color-${cat.key}`}
                  style={{
                    position: "relative", width: 16, height: 16, flexShrink: 0,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", borderRadius: 50,
                  }}
                >
                  <span style={{
                    width: 9, height: 9, borderRadius: 50, background: color,
                    boxShadow: `0 0 8px ${color}66`,
                  }} />
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => updateCategoryColor(cat.key, e.target.value)}
                    aria-label={`Color for ${cat.label}`}
                    data-testid={`category-color-input-${cat.key}`}
                    style={{
                      position: "absolute", inset: 0, width: "100%", height: "100%",
                      opacity: 0, cursor: "pointer", border: "none", padding: 0, background: "transparent",
                    }}
                  />
                </label>
                <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {cat.label}
                </span>
                <span className="text-dim" style={{ fontSize: 11.5 }}>{cat.count}</span>
              </div>
            );
          })}
        </div>

        {/* CONNECTED SOURCES */}
        <div className="section-label">Connected sources</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }} data-testid="connected-sources">
          {sources.map((s) => (
            <div
              key={s.key}
              data-testid={`source-${s.key}`}
              title={s.detail}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "6px 10px", borderRadius: 8,
              }}
            >
              <span style={{
                width: 22, height: 22, borderRadius: 6,
                background: hexToRgba(s.color, 0.14),
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700, color: s.color,
                flexShrink: 0, border: `1px solid ${hexToRgba(s.color, 0.3)}`,
              }}>{s.letter}</span>
              <span style={{ flex: 1, fontSize: 12.5, color: "var(--ink-soft)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {s.label}
              </span>
              <span style={{
                fontSize: 9.5, letterSpacing: "0.14em", color: s.status_color, fontWeight: 600,
                display: "inline-flex", alignItems: "center", gap: 5,
              }}>
                <span style={{ width: 5, height: 5, borderRadius: 50, background: s.status_color, display: "inline-block" }} />
                {s.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* User footer */}
      <div className="sidebar-section" style={{ borderTop: "1px solid var(--hairline-soft)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 8px" }}>
          {user?.picture ? (
            <img src={user.picture} alt="" style={{ width: 32, height: 32, borderRadius: "50%" }} />
          ) : (
            <div className="avatar-circle" style={{ width: 32, height: 32, background: "var(--brand-gradient)" }}>
              {initials}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {user?.name}
            </div>
            <div className="text-dim" style={{ fontSize: 11 }}>{user?.plan || "Personal"}</div>
          </div>
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            className="btn btn-ghost"
            style={{ padding: 8 }}
            title={theme === "light" ? "Switch to dark" : "Switch to light"}
            aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
          >
            {theme === "light"
              ? <Moon size={17} strokeWidth={2} />
              : <Sun size={17} strokeWidth={2} />}
          </button>
          <button onClick={logout} data-testid="logout-btn" className="btn btn-ghost" style={{ padding: 8 }} title="Sign out">
            <LogOut size={17} strokeWidth={2} />
          </button>
        </div>
      </div>
    </aside>
  );
}
